import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Typography,
} from "@material-ui/core";
import SaveIcon from "@material-ui/icons/Save";
import CancelIcon from "@material-ui/icons/Cancel";
import { useStyles } from "./styles";

const TYPES = [
  { value: "dollar_off", label: "$ Off Amount" },
  { value: "percent_off", label: "% Off Amount" },
];

const RewardModal = ({ open, onClose, onSave, tier }) => {
  const classes = useStyles();
  const [points, setPoints] = useState("");
  const [amount, setAmount] = useState("");
  const [type, setType] = useState("dollar_off");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      if (tier) {
        setPoints(String(tier.points));
        setAmount(String(tier.amount));
        setType(tier.type || "dollar_off");
      } else {
        setPoints("");
        setAmount("");
        setType("dollar_off");
      }
      setErrors({});
    }
  }, [open, tier]);

  const validatePoints = (val) => {
    if (val && !/^\d+$/.test(val)) {
      setErrors((prev) => ({ ...prev, points: "Points must be a positive integer" }));
    } else {
      setErrors((prev) => ({ ...prev, points: undefined }));
    }
  };

  const validateAmount = (val, currentType) => {
    if (val && !/^\d+$/.test(val)) {
      setErrors((prev) => ({ ...prev, amount: "Amount must be a positive integer" }));
    } else if (currentType === "percent_off" && val && parseInt(val, 10) > 100) {
      setErrors((prev) => ({ ...prev, amount: "% Amount must be between 1 and 100" }));
    } else {
      setErrors((prev) => ({ ...prev, amount: undefined }));
    }
  };

  const handleSave = () => {
    const newErrors = {};
    if (!points || !/^\d+$/.test(points) || parseInt(points, 10) < 1) {
      newErrors.points = "Points must be a positive integer";
    }
    if (!amount || !/^\d+$/.test(amount) || parseInt(amount, 10) < 1) {
      newErrors.amount = "Amount must be a positive integer";
    } else if (type === "percent_off" && parseInt(amount, 10) > 100) {
      newErrors.amount = "% Amount must be between 1 and 100";
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    onSave({
      points: parseInt(points, 10),
      amount: parseInt(amount, 10),
      type,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth className="reward-modal">
      <DialogTitle className="reward-modal-title">{tier ? "Edit Reward" : "Add Reward"}</DialogTitle>
      <DialogContent className="reward-modal-content">
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={3}>
            <Typography variant="caption">Points</Typography>
            <TextField
              fullWidth
              size="small"
              value={points}
              onChange={(e) => {
                setPoints(e.target.value);
                validatePoints(e.target.value);
              }}
              error={Boolean(errors.points)}
              helperText={errors.points}
              className="reward-modal-points-field"
              data-id="reward-points"
              data-testid="reward-points-input"
            />
          </Grid>
          <Grid item className={classes.modalEqualsSeparator}>
            <span>=</span>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="caption">Amount</Typography>
            <TextField
              fullWidth
              size="small"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                validateAmount(e.target.value, type);
              }}
              error={Boolean(errors.amount)}
              helperText={errors.amount}
              className="reward-modal-amount-field"
              data-id="reward-amount"
              data-testid="reward-amount-input"
            />
          </Grid>
          <Grid item xs>
            <Typography variant="caption">Type</Typography>
            <TextField
              fullWidth
              select
              size="small"
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="reward-modal-type-field"
              data-id="reward-type"
              data-testid="reward-type-select"
            >
              {TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value} data-id={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className={`${classes.modalActions} reward-modal-actions`}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          className="reward-modal-save-btn"
          data-id="reward-save"
          data-testid="reward-save-button"
        >
          Save
        </Button>
        <Button
          variant="contained"
          startIcon={<CancelIcon />}
          onClick={onClose}
          className={`${classes.modalCancelBtn} reward-modal-cancel-btn`}
          data-id="reward-cancel"
          data-testid="reward-cancel-button"
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RewardModal;
